import React, { useEffect, useRef } from 'react';
import './StorefrontAd.css';

/**
 * BannerAd - Image banner ad component
 * Supports industry standard sizes with optional text overlay
 */

// Banner size class mapping
const BANNER_SIZE_CLASSES = {
  'BILLBOARD_970x250': 'banner-billboard',
  'LEADERBOARD_728x90': 'banner-leaderboard',
  'MEDIUM_RECTANGLE_300x250': 'banner-medium-rectangle',
  'LARGE_MOBILE_320x100': 'banner-large-mobile',
  'MOBILE_LEADERBOARD_320x50': 'banner-mobile-leaderboard'
};

const BannerAd = ({
  ad,
  onView,
  onClick,
  className = '',
  showOverlay = true
}) => {
  const adRef = useRef(null);
  const viewTracked = useRef(false);

  // Track view when ad comes into viewport
  useEffect(() => {
    if (!ad || viewTracked.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewTracked.current) {
            viewTracked.current = true;
            if (onView) {
              onView(ad.id);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    if (adRef.current) {
      observer.observe(adRef.current);
    }

    return () => {
      if (adRef.current) {
        observer.unobserve(adRef.current);
      }
    };
  }, [ad, onView]);

  if (!ad) return null;

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick(ad.id, ad.ctaLink);
    }
  };

  const sizeClass = BANNER_SIZE_CLASSES[ad.mediaSize] || '';
  const accentColor = ad.accentColor || '#f97316';
  const hasMedia = ad.mediaUrl;
  const hasTextContent = ad.headline || ad.description;

  // If no media, render as a styled text banner
  if (!hasMedia) {
    const bgColor = ad.backgroundColor || '#1e293b';
    const textColor = ad.textColor || '#ffffff';

    return (
      <a
        ref={adRef}
        href={ad.ctaLink}
        onClick={handleClick}
        className={`banner-ad ad-fade-in ${sizeClass} ${className}`}
        style={{
          backgroundColor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          minHeight: '100px'
        }}
      >
        <div style={{ textAlign: 'center' }}>
          {ad.headline && (
            <h3
              className="banner-ad-headline"
              style={{
                color: textColor,
                position: 'relative',
                marginBottom: ad.description ? '0.5rem' : 0
              }}
            >
              {ad.headline}
            </h3>
          )}
          {ad.description && (
            <p
              className="banner-ad-description"
              style={{
                color: textColor,
                position: 'relative',
                marginBottom: ad.ctaText ? '1rem' : 0
              }}
            >
              {ad.description}
            </p>
          )}
          {ad.ctaText && (
            <span
              className="banner-ad-cta"
              style={{
                backgroundColor: accentColor,
                color: '#ffffff',
                position: 'relative'
              }}
            >
              {ad.ctaText}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </span>
          )}
        </div>

        {ad.sponsor && (
          <span className="banner-ad-sponsor">
            Ad by {ad.sponsor}
          </span>
        )}
      </a>
    );
  }

  // Image banner with optional overlay
  return (
    <a
      ref={adRef}
      href={ad.ctaLink}
      onClick={handleClick}
      className={`banner-ad ad-fade-in ${sizeClass} ${className}`}
    >
      <img
        src={ad.mediaUrl}
        alt={ad.headline || 'Advertisement'}
        className="banner-ad-image"
        loading="lazy"
      />

      {/* Text overlay for image banners */}
      {showOverlay && hasTextContent && (
        <div className="banner-ad-overlay">
          {ad.headline && (
            <h3 className="banner-ad-headline" style={{ color: ad.textColor || '#ffffff' }}>
              {ad.headline}
            </h3>
          )}
          {ad.description && (
            <p className="banner-ad-description" style={{ color: ad.textColor || '#ffffff' }}>
              {ad.description}
            </p>
          )}
          {ad.ctaText && (
            <span
              className="banner-ad-cta"
              style={{
                backgroundColor: accentColor,
                color: '#ffffff'
              }}
            >
              {ad.ctaText}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </span>
          )}
        </div>
      )}

      {ad.sponsor && (
        <span className="banner-ad-sponsor">
          Ad by {ad.sponsor}
        </span>
      )}
    </a>
  );
};

export default BannerAd;
